import { DeleteOutlined, SafetyOutlined } from '@ant-design/icons';
import { Button, List, Modal, Spin, message } from 'antd';
import moment from 'moment';
import React from 'react';

import {
  useGetTrustedDevicesQuery,
  useRevokeDeviceMutation,
  useRevokeAllDevicesMutation,
} from '../../../services/twoFactorApi';
import {
  Container,
  PageTitle,
  StyledCard,
  CardHeader,
  CardContent,
  DeviceItem,
  DeviceInfo,
  DeviceMeta,
} from './styles';

export function AccountSecurity() {
  const {
    data: devicesData,
    isLoading: isLoadingDevices,
    refetch: refetchDevices,
  } = useGetTrustedDevicesQuery();

  const devices = devicesData?.devices;
  const trustedDevicesEnabled = devicesData?.trusted_devices_enabled ?? true;

  const [revokeDevice] = useRevokeDeviceMutation();
  const [revokeAllDevices] = useRevokeAllDevicesMutation();

  const handleRevokeDevice = async (deviceId: number, deviceName: string) => {
    Modal.confirm({
      title: 'Revogar Dispositivo Confiável',
      content: `Tem certeza que deseja revogar "${deviceName}"? Este dispositivo precisará de verificação 2FA no próximo login.`,
      okText: 'Sim, revogar',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await revokeDevice(deviceId).unwrap();
          message.success('Dispositivo revogado com sucesso');
          refetchDevices();
        } catch (error: any) {
          message.error(error?.data?.error || 'Erro ao revogar dispositivo');
        }
      },
    });
  };

  const handleRevokeAllDevices = async () => {
    Modal.confirm({
      title: 'Revogar Todos os Dispositivos',
      content:
        'Tem certeza que deseja revogar todos os dispositivos confiáveis? Todos precisarão de verificação 2FA no próximo login.',
      okText: 'Sim, revogar todos',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const result = await revokeAllDevices().unwrap();
          message.success(result.message);
          refetchDevices();
        } catch (error: any) {
          message.error(error?.data?.error || 'Erro ao revogar dispositivos');
        }
      },
    });
  };

  if (isLoadingDevices) {
    return (
      <Container>
        <Spin size="large" />
      </Container>
    );
  }

  return (
    <Container>
      <PageTitle>Segurança da Conta</PageTitle>

      <StyledCard>
        <CardHeader>
          <div>
            <SafetyOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            <h3>Dispositivos Confiáveis</h3>
          </div>
          {trustedDevicesEnabled && devices && devices.length > 0 && (
            <Button danger size="small" onClick={handleRevokeAllDevices}>
              Revogar Todos
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {!trustedDevicesEnabled ? (
            <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
              Dispositivos confiáveis desativados neste ambiente
            </p>
          ) : (
            <>
              <p style={{ marginBottom: '16px', color: '#666' }}>
                Dispositivos que não precisam de código 2FA ao fazer login.
              </p>
              {!devices || devices.length === 0 ? (
                <p
                  style={{
                    textAlign: 'center',
                    color: '#999',
                    padding: '20px',
                  }}
                >
                  Nenhum dispositivo confiável registrado
                </p>
              ) : (
                <List
                  dataSource={devices}
                  renderItem={(device) => (
                    <DeviceItem>
                      <DeviceInfo>
                        <h4>{device.device_name}</h4>
                        <DeviceMeta>
                          <span>IP: {device.ip_address}</span>
                          <span>•</span>
                          <span>
                            Último acesso:{' '}
                            {moment(device.last_used_at).fromNow()}
                          </span>
                          <span>•</span>
                          <span>
                            Expira em: {moment(device.expires_at).fromNow()}
                          </span>
                        </DeviceMeta>
                      </DeviceInfo>
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          handleRevokeDevice(device.id, device.device_name)
                        }
                      >
                        Revogar
                      </Button>
                    </DeviceItem>
                  )}
                />
              )}
            </>
          )}
        </CardContent>
      </StyledCard>
    </Container>
  );
}

export default AccountSecurity;
